import { AxiosResponse } from "axios";
import { Dispatch } from "redux";
import axios from "../../axios";
import { AuthUser } from "../../classes/AuthUser";
import { auth as firebaseAuth } from "../../firebase";
import { signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, signOut } from "firebase/auth";
import { IAuthAction, IOrganization } from "../../interfaces";
import { clearImitateSession, IImitateSession, saveImitateSession, setImitateAllowWrite } from "../../shared/imitateStorage";
import { ActionTypes } from "./actionTypes";

const apiPath = "/json/auth";

const authStart = (): IAuthAction => {
  return {
    type: ActionTypes.AUTH_START,
  };
};

const authSuccess = (
  user: AuthUser | null,
  firebaseToken: string
): IAuthAction => {
  return {
    type: ActionTypes.AUTH_SUCCESS,
    user,
  };
};

const authFail = (error: string): IAuthAction => {
  return {
    type: ActionTypes.AUTH_FAIL,
    error,
  };
};

export const auth = (email: string, password: string, preferredLanguage: string) => {
  return async (dispatch: Dispatch) => {
    dispatch(authStart());
    try {
      await signInWithEmailAndPassword(firebaseAuth, email, password)
        .then(async (response) => {
          if (response.user) {
            const tokenResult = await response.user.getIdTokenResult();
            if (tokenResult) {
              const res = await axios.post(`${apiPath}/login`, { preferredLanguage });
              const loggedInUser = new AuthUser(res.data);

              dispatch(authSuccess(loggedInUser, tokenResult.token));
            } else {
              dispatch(authFail("error"));
            }
          } else {
            dispatch(authFail("error"));
          }
        })
        .catch((error) => {
          dispatch(authFail(error.message));
        });
    } catch ({ response }) {
      dispatch(authFail((response as AxiosResponse).data));
    }
  };
};

export const authWithGoogle = (preferredLanguage: string) => {
  return async (dispatch: Dispatch) => {
    dispatch(authStart());
    try {
      const provider = new GoogleAuthProvider();
      const response = await signInWithPopup(firebaseAuth, provider);
      if (response.user) {
        const tokenResult = await response.user.getIdTokenResult();
        if (tokenResult) {
          const res = await axios.post(`${apiPath}/login`, { preferredLanguage });
          const loggedInUser = new AuthUser(res.data);

          dispatch(authSuccess(loggedInUser, tokenResult.token));
        } else {
          dispatch(authFail("error"));
        }
      } else {
        dispatch(authFail("error"));
      }
    } catch (error) {
      dispatch(authFail((error as any).message || "error"));
    }
  };
};

const logoutStart = (): IAuthAction => {
  return {
    type: ActionTypes.AUTH_LOGOUT_START,
  };
};

const logoutSuccess = (): IAuthAction => {
  return {
    type: ActionTypes.AUTH_LOGOUT_SUCCESS,
  };
};

const logoutFail = (error: string): IAuthAction => {
  return {
    type: ActionTypes.AUTH_LOGOUT_FAIL,
    error,
  };
};

export const logout = () => {
  return async (dispatch: Dispatch) => {
    dispatch(logoutStart());
    try {
      clearImitateSession();
      await axios.post(`${apiPath}/logout`);
      await signOut(firebaseAuth);
      dispatch(logoutSuccess());
    } catch (error) {
      clearImitateSession();
      dispatch(logoutFail(error as string));
    }
  };
};

const getCurrentUserStart = (): IAuthAction => {
  return {
    type: ActionTypes.AUTH_GET_START,
  };
};

const getCurrentUserSuccess = (user: AuthUser | null): IAuthAction => {
  return {
    type: ActionTypes.AUTH_GET_SUCCESS,
    user,
  };
};

const getCurrentUserFail = (error: string): IAuthAction => {
  return {
    type: ActionTypes.AUTH_GET_FAIL,
    error,
  };
};

export const getCurrentUser = () => {
  return async (dispatch: Dispatch) => {
    dispatch(getCurrentUserStart());
    try {
      const res = await axios.post(`${apiPath}/login`);
      dispatch(getCurrentUserSuccess(new AuthUser(res.data)));
    } catch ({ response }) {
      dispatch(getCurrentUserFail((response as AxiosResponse).data));
    }
  };
};

const getCustomTokenStart = (): IAuthAction => {
  return {
    type: ActionTypes.AUTH_GET_CUSTOM_TOKEN_START,
  };
};

const getCustomTokenSuccess = (customToken: string): IAuthAction => {
  return {
    type: ActionTypes.AUTH_GET_CUSTOM_TOKEN_SUCCESS,
    customToken
  };
};

const getCustomTokenFail = (error: string): IAuthAction => {
  return {
    type: ActionTypes.AUTH_GET_CUSTOM_TOKEN_FAIL,
    error,
  };
};

export const getCustomToken = (token: string) => {
  return async (dispatch: Dispatch) => {
    dispatch(getCustomTokenStart());
    try {
      const resp = await axios.get<string>(`${apiPath}/getCustomToken?token=${token}`);
      dispatch(getCustomTokenSuccess(resp.data));
    } catch ({ response }) {
      dispatch(getCustomTokenFail((response as AxiosResponse).data));
    }
  };
};

export const addAuthUserOfferRequestAccess = (offerRequestId: string): IAuthAction => {
  return {
    type: ActionTypes.AUTH_ADD_OFFER_REQUEST_ACCESS,
    offerRequestId
  };
};

export const setAuthUserBillingConfirmed = (): IAuthAction => {
  return {
    type: ActionTypes.AUTH_SET_BILLING_CONFIRMED,
  };
};

export const setAuthUserOrganization = (organization: IOrganization): IAuthAction => {
  return {
    type: ActionTypes.AUTH_SET_ORGANIZATION,
    organization
  };
};

export const setImitateSessionAction = (imitateSession: IImitateSession | null): IAuthAction => {
  return {
    type: ActionTypes.AUTH_SET_IMITATE_SESSION,
    imitateSession,
  };
};

export const clearImitateSessionAction = (): IAuthAction => {
  return {
    type: ActionTypes.AUTH_CLEAR_IMITATE_SESSION,
  };
};

export const setImitateAllowWriteAction = (allowWrite: boolean): IAuthAction => {
  return {
    type: ActionTypes.AUTH_SET_IMITATE_ALLOW_WRITE,
    imitateSession: setImitateAllowWrite(allowWrite),
  };
};

export const startImpersonation = (session: IImitateSession) => {
  return (dispatch: Dispatch) => {
    saveImitateSession(session);
    dispatch(setImitateSessionAction(session));
  };
};

export const stopImpersonation = () => {
  return (dispatch: Dispatch) => {
    clearImitateSession();
    dispatch(clearImitateSessionAction());
  };
};